import { useState, FC, useRef, RefObject, useEffect } from 'react';
import {
    Button, Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Alert, AlertTitle
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { zodResolver } from '@hookform/resolvers/zod';
import { normalizeString } from 'utils/Validations'
import { TimekeeperService } from 'services/TimekeeperService';
import {
    AccrualRuleCreate,
    AccrualRuleRGrantCreate,
    AccrualRuleRLimitCreate,
    AccrualRuleRUseBalanceCreate,
    AccrualRule,
} from 'repository/timekeeper/@types/AccrualRule';
import { AccrualRuleForm, AccrualRuleFormData } from './form';
import { accrualRuleSchema } from './accrualFormSchema';
import { useAccrualRuleData } from './hooks/useAccrualRuleData';

const timekeeperService = new TimekeeperService();

interface CreateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: AccrualRule[];
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}


export const CreateAccrualRule: FC<CreateProps> = ({ open, setOpen, rows, reloading }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();
    const { availableData, loadingStates, loadData } = useAccrualRuleData();


    // React Hook Form
    const form = useForm<AccrualRuleFormData>({
        resolver: zodResolver(accrualRuleSchema(t, rows)),
        defaultValues: {
            name: "",
            description: "",
            adjustBalance: false,
            accrualCodeId: "" as number | "",
            accrualSurplusId: null,
            referenceDateServiceId: null,
        },
    });

    // Estado de las relaciones (grants, limits, use_balances)
    const [relationsData, setRelationsData] = useState({
        grants: [{ grant_id: 0, grant_amount: 0 }] as AccrualRuleRGrantCreate[],
        limits: [{ limit_id: 0, limit_amount: 0, limit_amount_unit: null }] as AccrualRuleRLimitCreate[],
        useBalances: [] as AccrualRuleRUseBalanceCreate[],
    });
    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)

    useEffect(() => {
        if (!open) return;
        loadData();
    }, [open, loadData]);

    const setUseBalances = (useBalances: AccrualRuleRUseBalanceCreate[]) => {
        setRelationsData((prev) => ({ ...prev, useBalances }));
    };

    const existName = (name: string) => {
        const normalizedInput = normalizeString(name);
        return  rows.some((row) => normalizeString(row.name) === normalizedInput);
    };

    const changes = () => {
        if (form.watch('name')) setAlerts(true)
        else setOpen(false);
    }

    const closeForm = () => {
        form.reset();
        setRelationsData({
            grants: [{ grant_id: 0, grant_amount: 0 }],
            limits: [{ limit_id: 0, limit_amount: 0, limit_amount_unit: '' }],
            useBalances: []
        });
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }

    const Create = form.handleSubmit(async (formData) => {
        setLoading(true);

        // Filtrar grants eliminando grant_id = 0
        const filteredGrants = relationsData.grants
            .filter(g => g.grant_id && g.grant_id > 0);

        // Filtrar limits eliminando limit_id = 0
        const filteredLimits = relationsData.limits
            .filter(l => l.limit_id && l.limit_id > 0);

        // Filtrar use_balances eliminando paycode_id = 0
        const filteredUseBalances = relationsData.useBalances
            .filter(ub => ub.paycode_id && ub.paycode_id > 0);

        const dataToCreate: AccrualRuleCreate = {
            name: formData.name,
            description: formData.description || null,
            adjust_balance: formData.adjustBalance,
            accrual_code_id: formData.accrualCodeId as number,
            accrual_surplus_id: formData.accrualSurplusId,
            reference_date_service_id: formData.referenceDateServiceId,
            grants: filteredGrants.length > 0 ? filteredGrants : [],
            limits: filteredLimits.length > 0 ? filteredLimits : [],
            use_balances: filteredUseBalances.length > 0 ? filteredUseBalances : []
        }

        try {
            console.log('Accrual Rule to create:', dataToCreate);
            const createdRule = await timekeeperService.accrual_rule.create(dataToCreate);
            console.log('Created Accrual Rule:', createdRule);

            enqueueSnackbar(t('accruals.accrual_rules.dialog.alerts.add.success'), { variant: 'success' });
            closeForm();
            reloading();

        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.accrual_rules.dialog.alerts.add.error'), { variant: 'error' });
            setLoading(false);
        }
    });

    const nameValue = form.watch('name');
    const accrualCodeIdValue = form.watch('accrualCodeId');
    const isSubmitDisabled = loading || !nameValue || !accrualCodeIdValue || accrualCodeIdValue === 0 || existName(nameValue);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_rules.dialog.titles.add')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <AccrualRuleForm
                    form={form}
                    relationsData={relationsData}
                    setRelationsData={setRelationsData}
                    availableData={availableData}
                    loadingStates={loadingStates}
                    existName={existName}
                    setUseBalances={setUseBalances}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} variant='outlined' disabled={loading}>
                    {t('general.cancel')}
                </Button>
                <Button
                    onClick={Create}
                    variant='contained'
                    disabled={isSubmitDisabled}
                >
                    {t('general.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
